import { FOOD_CUISINES, type FoodCuisineName } from '@steado/admin-contract/foods';
import {
  MEAL_PATTERN_SLOTS,
  PATTERN_COMPONENT_ROLES,
  type AdminMealPattern,
  type AdminMealPatternComponent,
  type AdminMealPatternPreview,
  type LIVE_MEAL_PATTERN_STATUSES,
  type MealPatternSlotName,
  type PatternComponentRoleName,
} from '@steado/admin-contract/meal-patterns';
import { DIET_TYPES, type DietTypeName } from '@steado/admin-contract/users';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Drawer } from '@/components/shell/Drawer';
import { BadgeCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { formatDateTime, humanise } from '@/lib/format';
import { Field, Fields, Panel } from '@/components/panel/panels';

import {
  activatePattern,
  createPattern,
  mealPatternsQueryOptions,
  previewPattern,
  retirePattern,
  savePatternVersion,
} from './meal-patterns-query';
import { SEEDED_TARGET_PREFIX } from './meal-patterns-search';
import { PATTERN_STATUS_TONES, describeComponents, isLiveStatus } from './pattern-tone';

type LiveStatus = (typeof LIVE_MEAL_PATTERN_STATUSES)[number];

interface PatternDrawerProps {
  /**
   * A version's id, the literal `new`, or `seed:<name>` for one of the shapes
   * the API ships with, which has no row and therefore no id.
   */
  readonly patternId: string;
  readonly onClose: () => void;
}

/** One "where this shape asks for A, B also works" row. */
interface SubstitutionRow {
  from: PatternComponentRoleName;
  to: PatternComponentRoleName;
}

interface DraftState {
  slug: string;
  slot: MealPatternSlotName;
  dietVariants: DietTypeName[];
  cuisines: FoodCuisineName[];
  components: AdminMealPatternComponent[];
  substitutions: SubstitutionRow[];
  maxPerWeek: string;
  evidence: string;
}

const BLANK: DraftState = {
  slug: '',
  slot: 'lunch',
  dietVariants: [...DIET_TYPES],
  cuisines: [],
  components: [{ role: 'any_dish', required: true, min: 1, max: 1 }],
  substitutions: [],
  maxPerWeek: '',
  evidence: '',
};

/** The wire caps cuisines at four; the form refuses the fifth rather than letting the API do it. */
const CUISINE_LIMIT = 4;

/**
 * One shape: its versions, its editor, and what turning it on would mean.
 *
 * The drawer is built around one rule the API also enforces and which is the
 * whole reason activate is a separate button: **saving is never activating.**
 * Editing writes the next version as a draft and leaves the live one exactly
 * as it was, so nothing an operator types in this form reaches plan generation
 * until they press Activate and say who is signing it.
 *
 * Both live-grammar actions go through a confirmation, and neither is a
 * click-through one. Activating changes what real users are served on their
 * next generated week and retires whichever version was live; retiring takes a
 * shape out of the grammar, which is the one direction that *narrows* what a
 * meal may be. Each dialog names the specific consequence rather than asking
 * "are you sure".
 */
export function PatternDrawer({ patternId, onClose }: PatternDrawerProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Three modes, and only the third can be edited in place.
   *
   * `seeded` is the interesting one: the shape is in force and there is no row
   * behind it, so nothing here can save over it. What the drawer offers
   * instead is the only thing that would change it — writing a curated
   * shape of the same name, prefilled with what the seeded one says today.
   */
  const seededSlug = patternId.startsWith(SEEDED_TARGET_PREFIX)
    ? patternId.slice(SEEDED_TARGET_PREFIX.length)
    : null;
  const isSeeded = seededSlug !== null;
  const isNew = patternId === 'new' || isSeeded;

  // Every version, so the drawer can show history without a second endpoint:
  // the list is a few dozen rows and the screen already holds it.
  const all = useQuery(mealPatternsQueryOptions(api, { history: true }));
  const seeded = useMemo(
    () => (all.data ?? []).find((row) => row.origin === 'seeded' && row.slug === seededSlug),
    [all.data, seededSlug],
  );
  const pattern = useMemo(
    () => (all.data ?? []).find((row) => row.id !== null && row.id === patternId),
    [all.data, patternId],
  );
  const versions = useMemo(
    () =>
      pattern === undefined
        ? []
        : (all.data ?? [])
            .filter((row) => row.origin === 'curated' && row.slug === pattern.slug)
            .sort((a, b) => (b.version ?? 0) - (a.version ?? 0)),
    [all.data, pattern],
  );
  /** What activating this one would replace — named in the dialog, not implied. */
  const liveSibling = useMemo(
    () => versions.find((row) => row.id !== patternId && isLiveStatus(row.status)),
    [versions, patternId],
  );

  const [draft, setDraft] = useState<DraftState | null>(patternId === 'new' ? BLANK : null);
  const [reviewer, setReviewer] = useState('');
  const [preview, setPreview] = useState<AdminMealPatternPreview | null>(null);
  const [confirming, setConfirming] = useState<LiveStatus | 'retire' | null>(null);
  const [retireReason, setRetireReason] = useState('');

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'meal-patterns'] });
  }, [queryClient]);

  const runPreview = useMutation({
    mutationFn: (id: string) => previewPattern(api, id),
    onSuccess: setPreview,
    retry: false,
  });

  const save = useMutation({
    mutationFn: (state: DraftState): Promise<AdminMealPattern> => {
      const facts = {
        slot: state.slot,
        dietVariants: state.dietVariants,
        cuisines: state.cuisines,
        components: state.components,
        substitutions: toWireSubstitutions(state.substitutions),
        maxPerWeek: state.maxPerWeek === '' ? null : Number(state.maxPerWeek),
        evidence: state.evidence,
      };
      return isNew
        ? createPattern(api, { slug: state.slug, ...facts })
        : savePatternVersion(api, patternId, facts);
    },
    onSuccess: (saved) => {
      invalidate();
      setDraft(null);
      if (isNew) onClose();
      else if (saved.id !== null) runPreview.mutate(saved.id);
    },
    retry: false,
  });

  const activate = useMutation({
    mutationFn: (status: LiveStatus) =>
      activatePattern(api, patternId, { status, reviewer: reviewer.trim() }),
    onSuccess: () => {
      invalidate();
      setConfirming(null);
    },
    retry: false,
  });

  const retire = useMutation({
    mutationFn: (reason: string) => retirePattern(api, patternId, { reason }),
    onSuccess: () => {
      invalidate();
      setConfirming(null);
      setRetireReason('');
    },
    retry: false,
  });

  const disabledTitle = can.mutate ? undefined : 'Editing the meal grammar requires the admin role.';
  const error = save.error ?? runPreview.error;
  // A seeded shape's own values are the starting point for the override.
  const editing = draft ?? toDraft(isSeeded ? seeded : pattern);
  const unsigned = reviewer.trim().length < 2;

  return (
    <Drawer
      title={
        isSeeded ? `Override ${seededSlug}` : isNew ? 'New meal pattern' : (pattern?.slug ?? 'Meal pattern')
      }
      subtitle={
        isSeeded
          ? 'A shape the API ships with. It has no version to edit, so saving writes a curated shape of the same name that takes its place.'
          : pattern === undefined
            ? 'One shape a household serves a meal in.'
            : `v${String(pattern.version ?? 0)} · ${humanise(pattern.status)} · authored by ${pattern.createdBy}`
      }
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!can.mutate || editing === null || save.isPending}
            title={disabledTitle}
            onClick={() => {
              if (editing !== null) save.mutate(editing);
            }}
          >
            {isSeeded ? 'Create an override draft' : isNew ? 'Create draft' : 'Save as next version'}
          </Button>
          {pattern === undefined ? null : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!can.mutate || unsigned}
                title={disabledTitle ?? (unsigned ? 'Name a reviewer first.' : undefined)}
                onClick={() => {
                  setConfirming('active_provisional');
                }}
              >
                Activate
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!can.mutate || unsigned}
                title={disabledTitle ?? 'Only where a dietitian has actually signed this shape.'}
                onClick={() => {
                  setConfirming('expert_reviewed');
                }}
              >
                Activate as expert-reviewed
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!can.mutate || pattern.status === 'retired'}
                title={disabledTitle}
                onClick={() => {
                  setConfirming('retire');
                }}
                className="ml-auto text-err-fg"
              >
                Retire
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {error === null ? null : (
          <Alert tone="err">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <Panel
          title="Shape"
          description="How many of each role this slot may carry. A drink, a pickle or a sweet rides alongside any shape and is capped elsewhere, so a pattern never counts one."
        >
          {editing === null ? null : (
            <div className="flex flex-col gap-4">
              {isNew ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pattern-slug">Name</Label>
                  <Input
                    id="pattern-slug"
                    value={editing.slug}
                    placeholder="dinner-staple-and-sabzi"
                    // Locked on an override: the name is the whole mechanism by
                    // which this shape takes the seeded one's place, so letting
                    // it be edited would quietly turn a replacement into an
                    // addition.
                    readOnly={isSeeded}
                    onChange={(event) => {
                      setDraft({ ...editing, slug: event.target.value });
                    }}
                  />
                  <p className="text-[13px] text-muted-foreground">
                    {isSeeded
                      ? 'Kept as it is: matching the built-in name is what makes this replace it rather than add to it.'
                      : 'Reusing a built-in name replaces that shape; a new name adds one alongside it.'}
                  </p>
                </div>
              ) : null}

              <ChipGroup
                label="Slot"
                values={MEAL_PATTERN_SLOTS}
                selected={[editing.slot]}
                onToggle={(slot) => {
                  setDraft({ ...editing, slot });
                }}
              />

              <ChipGroup
                label="Diets this shape exists for"
                values={DIET_TYPES}
                selected={editing.dietVariants}
                onToggle={(diet) => {
                  setDraft({
                    ...editing,
                    dietVariants: editing.dietVariants.includes(diet)
                      ? editing.dietVariants.filter((d) => d !== diet)
                      : [...editing.dietVariants, diet],
                  });
                }}
              />

              <ChipGroup
                label="Cuisines"
                hint="Leave empty for a shape every table serves. A tagged shape only applies to a meal carrying that tag, so it can only ever widen what that meal may be. At most four."
                values={FOOD_CUISINES}
                selected={editing.cuisines}
                disabledValue={(cuisine) =>
                  editing.cuisines.length >= CUISINE_LIMIT && !editing.cuisines.includes(cuisine)
                }
                onToggle={(cuisine) => {
                  setDraft({
                    ...editing,
                    cuisines: editing.cuisines.includes(cuisine)
                      ? editing.cuisines.filter((c) => c !== cuisine)
                      : [...editing.cuisines, cuisine],
                  });
                }}
              />

              <ComponentEditor
                components={editing.components}
                onChange={(components) => {
                  setDraft({ ...editing, components });
                }}
              />

              <SubstitutionEditor
                rows={editing.substitutions}
                onChange={(substitutions) => {
                  setDraft({ ...editing, substitutions });
                }}
              />

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pattern-frequency">Times a week</Label>
                <Input
                  id="pattern-frequency"
                  inputMode="numeric"
                  value={editing.maxPerWeek}
                  placeholder="3"
                  onChange={(event) => {
                    setDraft({ ...editing, maxPerWeek: event.target.value });
                  }}
                />
                <p className="text-[13px] text-muted-foreground">
                  Stated to the model and never enforced. A week that repeats this shape is not sent back for
                  repair.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pattern-evidence">Evidence</Label>
                <Textarea
                  id="pattern-evidence"
                  rows={3}
                  value={editing.evidence}
                  placeholder="ICMR-NIN Dietary Guidelines for Indians (2024), My Plate for the Day …"
                  onChange={(event) => {
                    setDraft({ ...editing, evidence: event.target.value });
                  }}
                />
                <p className="text-[13px] text-muted-foreground">
                  Cite it. &ldquo;It is what people eat&rdquo; is not evidence.
                </p>
              </div>
            </div>
          )}
        </Panel>

        {pattern === undefined ? null : (
          <>
            <Panel
              title="Sign-off"
              description="Activating retires whichever version of this name was live, and changes what plan generation enforces."
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pattern-reviewer">Reviewer</Label>
                <Input
                  id="pattern-reviewer"
                  value={reviewer}
                  placeholder="who is signing this shape"
                  onChange={(event) => {
                    setReviewer(event.target.value);
                  }}
                />
              </div>
            </Panel>

            <Panel
              title="Impact"
              description="Counted against real committed meals in this slot, using the generator's own matching."
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={runPreview.isPending}
                  onClick={() => {
                    // Non-null by construction: this panel only renders for a
                    // saved version, and a seeded shape never becomes one.
                    if (pattern.id !== null) runPreview.mutate(pattern.id);
                  }}
                >
                  {runPreview.isPending ? 'Measuring…' : 'Measure'}
                </Button>
              }
            >
              {preview === null ? (
                <p className="text-[13px] text-muted-foreground">
                  Nothing measured yet. Patterns are alternatives, so adding one can only lower the rejection
                  count and retiring one can only raise it.
                </p>
              ) : (
                <Fields>
                  <Field label="Weeks sampled" value={String(preview.weeks)} mono />
                  <Field label={`Meals in ${pattern.slot}`} value={String(preview.mealsInSlot)} mono />
                  <Field label="Have room under this shape" value={String(preview.matched)} mono />
                  <Field
                    label="Would be sent back for repair"
                    value={`${String(preview.wouldReject)} (${String(preview.wouldRejectWithout)} as things stand today)`}
                    mono
                  />
                  <Field
                    label="Examples"
                    value={
                      preview.samples.length === 0
                        ? '—'
                        : preview.samples
                            .map(
                              (sample) =>
                                `${sample.weekStartDate} ${sample.dayOfWeek}: ${sample.roles.join(', ')}`,
                            )
                            .join(' · ')
                    }
                  />
                </Fields>
              )}
            </Panel>

            <Panel
              title="Versions"
              description="A pattern is never edited in place; every save is a new version."
            >
              <ul className="flex flex-col gap-2">
                {versions.map((version) => (
                  <li
                    key={version.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2"
                  >
                    <span className="font-mono text-[13px] text-foreground">
                      v{String(version.version ?? 0)}
                    </span>
                    <BadgeCell
                      label={humanise(version.status)}
                      tone={PATTERN_STATUS_TONES[version.status]}
                      dot
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">
                      {describeComponents(version.components)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {version.reviewer ?? version.createdBy}
                      {version.createdAt === null ? '' : ` · ${formatDateTime(version.createdAt)}`}
                    </span>
                    {isLiveStatus(version.status) ? (
                      <span className="text-xs font-medium text-primary">in force</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Panel>
          </>
        )}
      </div>

      {confirming === null || pattern === undefined ? null : confirming === 'retire' ? (
        <ConfirmDialog
          title={`Retire ${pattern.slug}?`}
          body={
            <span>
              This takes the shape out of the grammar, which is the one direction that narrows what a meal may
              be: fewer shapes means more meals sent back for repair. If this name is also one of the seeded
              shapes, retiring it here is what removes it. The reason goes on the audit row, not on the
              pattern.
              <span className="mt-3 flex flex-col gap-1.5">
                <Label htmlFor="retire-reason">Why</Label>
                <Input
                  id="retire-reason"
                  value={retireReason}
                  placeholder="Superseded by dinner-thali v3"
                  onChange={(event) => {
                    setRetireReason(event.target.value);
                  }}
                />
              </span>
            </span>
          }
          confirmLabel="Retire"
          destructive
          confirmDisabled={retireReason.trim().length < 3}
          isPending={retire.isPending}
          error={retire.error?.message}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            retire.mutate(retireReason.trim());
          }}
        />
      ) : (
        <ConfirmDialog
          title={`Put v${String(pattern.version ?? 0)} of ${pattern.slug} in force?`}
          body={
            <span>
              Plan generation will judge every {humanise(pattern.slot).toLowerCase()} against this shape from
              the next generated week, and the model will be asked for it by name.{' '}
              {liveSibling === undefined
                ? 'Nothing of this name is live today, so this adds a shape, or replaces the seeded one that shares its name.'
                : `v${String(liveSibling.version ?? 0)} is live today and will be retired.`}{' '}
              Signed by {reviewer.trim()}
              {confirming === 'expert_reviewed'
                ? ', recorded as expert-reviewed, which claims a dietitian has actually signed it.'
                : ', recorded as provisionally active.'}
            </span>
          }
          confirmLabel={confirming === 'expert_reviewed' ? 'Activate as expert-reviewed' : 'Activate'}
          isPending={activate.isPending}
          error={activate.error?.message}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            activate.mutate(confirming);
          }}
        />
      )}
    </Drawer>
  );
}

interface ChipGroupProps<T extends string> {
  readonly label: string;
  readonly hint?: string;
  readonly values: readonly T[];
  readonly selected: readonly T[];
  readonly disabledValue?: (value: T) => boolean;
  readonly onToggle: (value: T) => void;
}

/** The one multi-select shape this form uses, so slot, diets and cuisines cannot drift apart. */
function ChipGroup<T extends string>({
  label,
  hint,
  values,
  selected,
  disabledValue,
  onToggle,
}: ChipGroupProps<T>) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
        {values.map((value) => {
          const on = selected.includes(value);
          return (
            <Button
              key={value}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={on}
              disabled={disabledValue?.(value) ?? false}
              onClick={() => {
                onToggle(value);
              }}
              className={cn(on ? 'border-primary text-primary' : 'text-muted-foreground')}
            >
              {humanise(value)}
            </Button>
          );
        })}
      </div>
      {hint === undefined ? null : <p className="text-[13px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

interface ComponentEditorProps {
  readonly components: readonly AdminMealPatternComponent[];
  readonly onChange: (next: AdminMealPatternComponent[]) => void;
}

function ComponentEditor({ components, onChange }: ComponentEditorProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label>Components</Label>
      {components.map((component, index) => (
        <div key={`${component.role}-${String(index)}`} className="flex flex-wrap items-center gap-2">
          <RoleSelect
            label={`Component ${String(index + 1)} role`}
            value={component.role}
            onChange={(role) => {
              onChange(components.map((c, i) => (i === index ? { ...c, role } : c)));
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={component.required}
            onClick={() => {
              onChange(
                components.map((c, i) =>
                  i === index ? { ...c, required: !c.required, min: c.required ? 0 : Math.max(1, c.min) } : c,
                ),
              );
            }}
            className={cn(component.required ? 'border-primary text-primary' : 'text-muted-foreground')}
          >
            {component.required ? 'required' : 'optional'}
          </Button>
          <NumberBox
            label="min"
            value={component.min}
            onChange={(min) => {
              onChange(components.map((c, i) => (i === index ? { ...c, min } : c)));
            }}
          />
          <NumberBox
            label="max"
            value={component.max}
            onChange={(max) => {
              onChange(components.map((c, i) => (i === index ? { ...c, max } : c)));
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onChange(components.filter((_, i) => i !== index));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            onChange([...components, { role: 'side', required: false, min: 0, max: 1 }]);
          }}
        >
          Add component
        </Button>
      </div>
    </div>
  );
}

interface SubstitutionEditorProps {
  readonly rows: readonly SubstitutionRow[];
  readonly onChange: (next: SubstitutionRow[]) => void;
}

/**
 * Substitutions as sentences rather than as the JSON object they serialise to.
 *
 * The wire shape is `{"vegetable": ["protein_anchor"]}`, which reads as
 * nothing at all on a form. What a curator is actually saying is "where this
 * plate asks for a sabzi, a dal also works", so that is the row.
 */
function SubstitutionEditor({ rows, onChange }: SubstitutionEditorProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label>Substitutions</Label>
      {rows.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          None. A substitution widens one component of this shape only — roti with a dal instead of the sabzi
          beside it.
        </p>
      ) : null}
      {rows.map((row, index) => (
        <div key={`${row.from}-${row.to}-${String(index)}`} className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] text-muted-foreground">where this asks for a</span>
          <RoleSelect
            label={`Substitution ${String(index + 1)} component`}
            value={row.from}
            onChange={(from) => {
              onChange(rows.map((r, i) => (i === index ? { ...r, from } : r)));
            }}
          />
          <span className="text-[13px] text-muted-foreground">a</span>
          <RoleSelect
            label={`Substitution ${String(index + 1)} stand-in`}
            value={row.to}
            onChange={(to) => {
              onChange(rows.map((r, i) => (i === index ? { ...r, to } : r)));
            }}
          />
          <span className="text-[13px] text-muted-foreground">also works</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onChange(rows.filter((_, i) => i !== index));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            onChange([...rows, { from: 'vegetable', to: 'protein_anchor' }]);
          }}
        >
          Add substitution
        </Button>
      </div>
    </div>
  );
}

function RoleSelect({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: PatternComponentRoleName;
  readonly onChange: (next: PatternComponentRoleName) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => {
        onChange(event.target.value as PatternComponentRoleName);
      }}
      className="h-9 rounded-md border border-border bg-background px-2 text-[13px]"
    >
      {PATTERN_COMPONENT_ROLES.map((role) => (
        <option key={role} value={role}>
          {role === 'any_dish' ? 'dish (any role)' : humanise(role)}
        </option>
      ))}
    </select>
  );
}

function NumberBox({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (next: number) => void;
}) {
  return (
    <label className="flex items-center gap-1 text-[13px] text-muted-foreground">
      {label}
      <Input
        inputMode="numeric"
        value={String(value)}
        onChange={(event) => {
          const next = Number(event.target.value);
          onChange(Number.isFinite(next) ? next : 0);
        }}
        className="h-9 w-14"
      />
    </label>
  );
}

/** Rows back to the wire's `{from: [to, …]}`; no rows means "none", not an empty object. */
function toWireSubstitutions(rows: readonly SubstitutionRow[]): AdminMealPattern['substitutions'] {
  if (rows.length === 0) return null;

  const merged: Record<string, PatternComponentRoleName[]> = {};
  for (const row of rows) {
    const existing = merged[row.from] ?? [];
    if (!existing.includes(row.to)) merged[row.from] = [...existing, row.to];
  }
  return merged;
}

function toDraft(pattern: AdminMealPattern | undefined): DraftState | null {
  if (pattern === undefined) return null;
  return {
    slug: pattern.slug,
    slot: pattern.slot,
    dietVariants: [...pattern.dietVariants],
    cuisines: [...pattern.cuisines],
    components: pattern.components.map((component) => ({ ...component })),
    substitutions: Object.entries(pattern.substitutions ?? {}).flatMap(([from, to]) =>
      to.map((role) => ({ from: from as PatternComponentRoleName, to: role })),
    ),
    maxPerWeek: pattern.maxPerWeek === null ? '' : String(pattern.maxPerWeek),
    evidence: pattern.evidence,
  };
}
